import { Component, type ErrorInfo, type ReactNode } from 'react'

import { CrashPage } from '@/routes/CrashPage'

/* The last line of defence. Everything else in feedback/ handles errors the
 * application EXPECTED — a rejected request, a lost connection, a 403. This
 * catches the ones it did not: a render that threw, a selector that read
 * through a null, a mapper that met a shape the server had never sent before.
 *
 * React gives exactly one mechanism for this and it is a class component.
 * There is no hook equivalent, and `react-error-boundary` would be a
 * dependency for forty lines we can read ourselves.
 *
 * What a boundary does NOT catch, so nobody plans around a guarantee that is
 * not there: event handlers, effects that throw asynchronously, timers,
 * promise rejections, and errors during server rendering. Those still reach
 * window.onerror. Query and mutation failures never reach here either — they
 * are values TanStack Query hands back, and ErrorState renders them where the
 * data was going to be, which is a better place to recover than a whole page.
 */

export interface ErrorBoundaryProps {
  children: ReactNode
  /** Rendered instead of the crash page — a section can degrade in place. */
  fallback?: (error: Error, reset: () => void) => ReactNode
  /** Reported alongside the error so a log line says WHICH boundary caught. */
  label?: string
  /* Changing this resets the boundary. A crash caught on /cases/17 would
     otherwise still be showing when the user navigates to /finance: the
     boundary holds error state, and routing alone does not clear it. The
     router-level boundary passes the pathname. */
  resetKey?: string
}

interface ErrorBoundaryState {
  error: Error | null
  /* Bumped on every reset so the subtree REMOUNTS rather than re-rendering
     with whatever state made it throw. Without it, a child that crashed on a
     bad value in its own state throws again on the first render after reset,
     and "Try again" looks broken. */
  attempt: number
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null, attempt: 0 }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error }
  }

  componentDidUpdate(prev: ErrorBoundaryProps) {
    // Navigating away from a crashed page clears it. Guarded on `error` so a
    // routine prop change on a healthy subtree is not a state update.
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.reset()
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    /* The stack is the only thing that makes a crash diagnosable, and in
       production the user cannot read the console. This is where a reporting
       service goes when there is one; until then the console entry is what a
       developer asks the user to screenshot.

       Logged unconditionally, not behind `import.meta.env.DEV`: a crash that
       only reports itself in development is a crash nobody hears about. */
    console.error(
      `[ErrorBoundary${this.props.label ? `: ${this.props.label}` : ''}]`,
      error,
      info.componentStack,
    )
  }

  reset = () => {
    this.setState((state) => ({ error: null, attempt: state.attempt + 1 }))
  }

  render() {
    const { error, attempt } = this.state
    const { children, fallback } = this.props

    if (error) {
      return fallback ? (
        fallback(error, this.reset)
      ) : (
        <CrashPage error={error} onReset={this.reset} />
      )
    }

    return <div key={attempt} className="contents">{children}</div>
  }
}
